import { Box, BoxProps, Tooltip, forwardRef } from '@eigtech/ui-shared-dave'
import { Button } from '@eigtech/ui/components/button'
import {
  useActiveSpeakersState,
  useContentShareState,
  useRemoteVideoTileState,
} from 'amazon-chime-sdk-component-library-react'
import { pick } from 'lodash-es'
import { Pin, PinOff } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useShallow } from 'zustand/shallow'
import { MeetingState, useMeetingStore } from '../../context/MeetingState'
import { useFilteredRoster } from '../../hooks/useRoster'
import { ContentShare } from './ContentShare'
import { DeprecatedVideoTile, DeprecatedVideoTileProps } from './DeprecatedVideoTile'
import { LocalVideo } from './LocalVideo'
import { RemoteVideo } from './RemoteVideo'

export type ActiveVideoProps = BoxProps

export const ActiveVideo = forwardRef<ActiveVideoProps, 'div'>(function ActiveVideo(props, ref) {
  const { chimeAttendee, participant, selectedSpeaker, setSelectedSpeaker } = useMeetingStore(
    useShallow(stateSelector)
  )
  const roster = useFilteredRoster()

  const activeSpeakers = useActiveSpeakersState()

  const { everyoneButCurrentUser, everyoneButCurrentUserDict } = useMemo(() => {
    const everyoneButCurrentUser = Object.entries(roster).filter(
      ([__, attendee]) => attendee.externalUserId !== participant?.email
    )
    const everyoneButCurrentUserDict = Object.fromEntries(everyoneButCurrentUser)

    return { everyoneButCurrentUser, everyoneButCurrentUserDict }
  }, [roster, participant])

  const getActiveSpeaker = useCallback(() => {
    if (!everyoneButCurrentUser.length) {
      return null
    }

    if (!activeSpeakers.length) {
      return everyoneButCurrentUser[0]?.[1] ?? null
    }

    return everyoneButCurrentUserDict[activeSpeakers[0]] ?? null
  }, [activeSpeakers, everyoneButCurrentUser, everyoneButCurrentUserDict])

  const [lastSpeaker, setLastSpeaker] = useState(getActiveSpeaker())

  useEffect(() => {
    setLastSpeaker((current) => {
      if (!everyoneButCurrentUser.length) return null

      const activeSpeaker = getActiveSpeaker()

      return activeSpeaker ?? current
    })
  }, [everyoneButCurrentUser, getActiveSpeaker, roster])

  const { attendeeIdToTileId } = useRemoteVideoTileState()

  const { tileId: contentTileId, sharingAttendeeId } = useContentShareState()

  const activeSpeaker = useMemo(() => {
    return selectedSpeaker && roster[selectedSpeaker]
      ? selectedSpeaker
      : lastSpeaker?.chimeAttendeeId
  }, [selectedSpeaker, roster, lastSpeaker])

  const activeSpeakerIsSelf = chimeAttendee?.AttendeeId === activeSpeaker

  const activeVideo = useMemo(() => {
    return activeSpeaker ? attendeeIdToTileId[activeSpeaker] : null
  }, [attendeeIdToTileId, activeSpeaker])

  const isPinned = !!selectedSpeaker
  const sharedProps: DeprecatedVideoTileProps = {
    className: 'w-full',
    actionsSlot: (
      <Tooltip label={`${isPinned ? 'Unpin' : 'Pin'} user`}>
        <Button
          aria-label={`${isPinned ? 'Unpin' : 'Pin'} user.`}
          className="size-[1.75em] text-white shadow-2xl"
          mode="icon"
          size="sm"
          variant="ghost"
          onClick={() => {
            const speaker = activeSpeaker ?? lastSpeaker?.chimeAttendeeId
            if (!speaker) return
            setSelectedSpeaker(isPinned ? null : speaker)
          }}
        >
          {isPinned ? (
            <Pin className="inherit size-[1em] text-[inherit]" />
          ) : (
            <PinOff className="inherit size-[1em] text-[inherit]" />
          )}
        </Button>
      </Tooltip>
    ),
  }

  return (
    <Box ref={ref} {...props}>
      {contentTileId ? (
        <ContentShare
          attendeeId={sharingAttendeeId ?? ''}
          className="w-full text-[clamp(1.2rem,1.5vw,2rem)]"
        />
      ) : (
        !!activeSpeaker &&
        (activeSpeakerIsSelf ? (
          <LocalVideo isActiveVideo={true} {...sharedProps} />
        ) : activeVideo ? (
          <RemoteVideo attendeeId={activeSpeaker} tileId={activeVideo} {...sharedProps} />
        ) : (
          <DeprecatedVideoTile attendeeId={activeSpeaker} {...sharedProps} />
        ))
      )}
    </Box>
  )
})

const stateSelector = (state: MeetingState) =>
  pick(state, ['chimeAttendee', 'participant', 'selectedSpeaker', 'setSelectedSpeaker'])
